import React, { Fragment, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Switch, Route, matchPath } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';
import commonHelmetJson from 'common-helmet-json';

import createStore from '@stencil/create-redux-store';
import Header from '@stencil/header';
import UserDetails from '@stencil/user-reducer/src/user-details';
import Footer from '@stencil/footer';
import Modal from '@stencil/modal';
import { defaultTheme } from '@stencil/build-theme';
import { GlobalStyles } from '@stencil/global-style';
import { withAppErrorBoundary } from '@stencil/app-error-boundary';

if (!String.prototype.replaceAll) {
  String.prototype.replaceAll = function (str, newStr) {
    // If a regex pattern
    if (Object.prototype.toString.call(str).toLowerCase() === '[object regexp]') {
      return this.replace(str, newStr);
    }

    // If a string
    return this.replace(new RegExp(str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g'), newStr);
  };
}

// Common user widget integeration
// Ping did mount integeration
// Component did catch error tracking
// Modal support

const withFlightsAppErrorBoundary = Component =>
  withAppErrorBoundary(Component, {
    appName: 'aerialClient'
  });

const App = ({ routes, store, theme, style }) => {
  let hideHeader = false;
  let hideFooter = false;
  let showModal = false;

  const RouteComponent = props => {
    const { location } = props;
    const { pathname } = location;
    const previousLocationRef = useRef();
    useEffect(() => {
      previousLocationRef.current = location;
    });
    const previousLocation = previousLocationRef.current;
    routes.some(route => {
      const matchObj = matchPath(pathname, route);
      if (matchObj) {
        const { showHeader = true, showFooter = true, isModal = false } = route;
        hideHeader = !showHeader;
        hideFooter = !showFooter;
        showModal = isModal;
      }
      return matchObj;
    });

    // let Header = () => <div></div>;
    // if (!hideHeader) {
    //   Header = require('@stencil/header').default;
    // }

    return (
      <Fragment>
        <UserDetails />
        <Header location={window.location} hideHeader={hideHeader} />
        {(showModal && (
          <Modal>
            <Switch>
              {routes
                .filter(route => route.isModal)
                .map(route => (
                  <Route {...route} />
                ))}
            </Switch>
          </Modal>
        )) ||
          null}
        <Switch location={showModal ? previousLocation : location}>
          {routes
            .filter(route => !route.isModal)
            .map(route => (
              <Route {...route} />
            ))}
        </Switch>
        {(!hideFooter && <Footer />) || null}
      </Fragment>
    );
  };

  let styleComponent = <GlobalStyles />;
  if (style) {
    styleComponent = style;
  }

  return (
    <ThemeProvider theme={theme}>
      <Helmet {...commonHelmetJson} />
      <Provider store={store}>
        {styleComponent}
        <BrowserRouter>
          <Route component={RouteComponent} />
        </BrowserRouter>
      </Provider>
    </ThemeProvider>
  );
};

const AppWithErrorBoundary = withFlightsAppErrorBoundary(App);

export default function mountReactApp({
  routes,
  reducers,
  initialState = window.__initial_state__,
  theme,
  style
}) {
  const store = createStore({ reducers, initialState });
  const urlPath = window.location.pathname;
  const queryParams = window.location.search;
  routes.some(route => {
    const matchObj = matchPath(urlPath, route);
    if (matchObj) {
      const { params = {} } = matchObj;
      const { populateStoreFromUrl = () => {} } = route;
      populateStoreFromUrl({ params, queryParams, searchString: queryParams }, store.dispatch);
    }
    return matchObj;
  });
  ReactDOM.hydrate(
    <AppWithErrorBoundary
      routes={routes}
      store={store}
      theme={theme || defaultTheme}
      style={style}
    />,
    document.getElementById('root')
  );
  window?.pageSeen && window.pageSeen('TTI');
}
